import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  Header,
  Title,
  Paper,
  Toggle,
  Input,
  Checkbox,
  SubmitActions,
  WithSpinner,
  Button,
  TitleSecondary,
} from '../../components';
import { config } from '../../api/config';
import { actions } from '../../state';
import { hasPermission } from '../../utils';

import styles from './affiliate.module.scss';

const LANG_FIELDS = [
  {
    name: 'description_lt',
    emoji: '🇱🇹',
    label: 'Lietuvių kalba',
  },
  {
    name: 'description_en',
    emoji: '🇬🇧',
    label: 'Anglų kalba',
  },
  {
    name: 'description_ru',
    emoji: '🇷🇺',
    label: 'Rusų kalba',
  },
];

const mapApiToForm = data => ({
  status: data.status === 1 ? 1 : 0,
  reward_amount_eur:
    data.reward_amount_eur != null ? String(data.reward_amount_eur) : '',
  required_spend_eur:
    data.required_spend_eur != null ? String(data.required_spend_eur) : '',
  validity_days: data.validity_days != null ? String(data.validity_days) : '',
  eligibility_max_spend_eur:
    data.eligibility_max_spend_eur != null
      ? String(data.eligibility_max_spend_eur)
      : '',
  sender_gets_reward: Boolean(data.sender_gets_reward),
  receiver_gets_reward: Boolean(data.receiver_gets_reward),
  description_lt: data.description_lt ?? '',
  description_en: data.description_en ?? '',
  description_ru: data.description_ru ?? '',
});

const AffiliateSettingsPage = ({
  dispatch,
  affiliate: { settings, settingsLoading, settingsSaving },
  permissions,
}) => {
  const [form, setForm] = useState(null);

  const canEdit = hasPermission(config.PERMISSIONS.affiliate.edit, permissions);

  const loadSettings = useCallback(() => {
    dispatch(actions.affiliate.getAffiliateSettingsAction());
  }, [dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (settings) {
      setForm(mapApiToForm(settings));
    }
  }, [settings]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleMaster = () => {
    setForm(prev => ({
      ...prev,
      status: prev.status === 1 ? 0 : 1,
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!canEdit || !form) return;

    const body = {
      status: form.status,
      reward_amount_eur: Number(form.reward_amount_eur) || 0,
      required_spend_eur: Number(form.required_spend_eur) || 0,
      validity_days: Number(form.validity_days) || 0,
      eligibility_max_spend_eur: Number(form.eligibility_max_spend_eur) || 0,
      sender_gets_reward: form.sender_gets_reward,
      receiver_gets_reward: form.receiver_gets_reward,
      description_lt: form.description_lt,
      description_en: form.description_en,
      description_ru: form.description_ru,
    };

    dispatch(actions.affiliate.updateAffiliateSettingsAction(body));
  };

  return (
    <section>
      <Header size="sm">
        <Title>{config.PAGES.affiliateSettings}</Title>
        {form && (
          <div className={styles.headerActions}>
            <div className={styles.activityToggleWrapper}>
              <Toggle
                onChange={toggleMaster}
                checked={form.status === 1}
                showMeta={false}
                disabled={!canEdit || settingsSaving}
              />
              <span className={styles.toggleLabel}>Įjungta</span>
            </div>
          </div>
        )}
      </Header>

      <WithSpinner loading={settingsLoading}>
        {form && (
          <Paper size="sm">
            <form className={styles.form} onSubmit={handleSubmit} method="POST">
              <div className={styles.sectionTitle}>
                <TitleSecondary>Nustatymai</TitleSecondary>
              </div>

              <div className={styles.inputsGrid}>
                <Input
                  name="reward_amount_eur"
                  label="Papildymas, EUR"
                  type="number"
                  step="any"
                  value={form.reward_amount_eur}
                  onChange={handleChange}
                  disabled={!canEdit || settingsSaving}
                />

                <Input
                  name="required_spend_eur"
                  label="Išleista suma, EUR"
                  type="number"
                  step="any"
                  value={form.required_spend_eur}
                  onChange={handleChange}
                  disabled={!canEdit || settingsSaving}
                />

                <Input
                  name="validity_days"
                  label="Galioja dienų (invite)"
                  type="number"
                  step="1"
                  value={form.validity_days}
                  onChange={handleChange}
                  disabled={!canEdit || settingsSaving}
                />

                <Input
                  name="eligibility_max_spend_eur"
                  label="Galioja išleidus nedaugiau nei €"
                  type="number"
                  step="any"
                  value={form.eligibility_max_spend_eur}
                  onChange={handleChange}
                  disabled={!canEdit || settingsSaving}
                />
              </div>

              <div className={styles.sectionTitle}>
                <TitleSecondary>Atlygiai</TitleSecondary>
              </div>

              <div className={styles.checkboxList}>
                <Checkbox
                  name="sender_gets_reward"
                  label="Siuntėjas gauna papildymą"
                  checked={form.sender_gets_reward}
                  onChange={handleChange}
                  disabled={!canEdit || settingsSaving}
                  dense
                  fullLine
                />

                <Checkbox
                  name="receiver_gets_reward"
                  label="Gavėjas gauna papildymą"
                  checked={form.receiver_gets_reward}
                  onChange={handleChange}
                  disabled={!canEdit || settingsSaving}
                  dense
                  fullLine
                />
              </div>

              <div className={styles.sectionTitle}>
                <TitleSecondary>Taisyklės</TitleSecondary>
              </div>

              <div className={styles.descriptions}>
                {LANG_FIELDS.map(({ name, emoji, label }) => (
                  <div className={styles.languageField} key={name}>
                    <div className={styles.languageLabel}>
                      <span className={styles.emoji} aria-hidden="true">
                        {emoji}
                      </span>
                      <label htmlFor={name}>{label}</label>
                    </div>
                    <Input
                      name={name}
                      type="textarea"
                      withLabel={false}
                      value={form[name]}
                      onChange={handleChange}
                      disabled={!canEdit || settingsSaving}
                    />
                  </div>
                ))}
              </div>

              <div className={styles.actions}>
                {canEdit ? (
                  <SubmitActions
                    back={config.APP_PATHS.affiliate}
                    loading={settingsSaving}
                    disabled={settingsSaving}
                  />
                ) : (
                  <Button color="dim" url={config.APP_PATHS.affiliate}>
                    Grįžti
                  </Button>
                )}
              </div>
            </form>
          </Paper>
        )}
      </WithSpinner>
    </section>
  );
};

const mapStateToProps = state => ({
  affiliate: state.affiliate,
  permissions: state.auth.permissions,
});

AffiliateSettingsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  affiliate: PropTypes.shape({
    settings: PropTypes.object,
    settingsLoading: PropTypes.bool,
    settingsSaving: PropTypes.bool,
  }).isRequired,
  permissions: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default connect(mapStateToProps)(AffiliateSettingsPage);
